import React, { useContext, useMemo } from 'react';
import { Window } from '@mui/icons-material';
import { UserAgentValuesContext } from 'contexts';

export const KeyLabel = {
  SHIFT: 'Shift',
  CTRL: 'Ctrl',
  OPT: 'Alt',
  CMD: 'Super',
};

export const Utf8KeyLabel = {
  SHIFT: '⇧',
  CTRL: '⌃',
  OPT: '⌥',
  CMD: '⌘',
};

/**
 * @typedef {Object} KeyboardKeyLabels
 * @property {ReactNode} ShiftKey - Shift key label
 * @property {ReactNode} CtrlKey - Control key label
 * @property {ReactNode} OptAltKey - Option or Alt key label
 * @property {ReactNode} CmdWinKey - Command or Windows key label
 */

/**
 * Hook that provides platform-specific keyboard key labels and representations.
 * @returns {KeyboardKeyLabels}
 */
export function useKeyboardKeyLabels() {
  const { userAgent } = useContext(UserAgentValuesContext) || { userAgent: {} };

  return useMemo(() => {
    const labels = {
      ShiftKey: KeyLabel.SHIFT,
      CtrlKey: KeyLabel.CTRL,
      OptAltKey: KeyLabel.OPT,
      CmdWinKey: KeyLabel.CMD,
    };
    if (userAgent?.isWindows) {
      labels.CmdWinKey = <Window fontSize="small" titleAccess="Windows Key" />;
      return labels;
    }
    if (userAgent?.isMac) {
      labels.ShiftKey = Utf8KeyLabel.SHIFT;
      labels.CtrlKey = Utf8KeyLabel.CTRL;
      labels.OptAltKey = Utf8KeyLabel.OPT;
      labels.CmdWinKey = Utf8KeyLabel.CMD;
      return labels;
    }
    return labels;
  }, [userAgent?.isWindows, userAgent?.isMac]);
}
